import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, CartContainer } from '@aimsos/ui';

import { NBA_TAG_VARIANTS } from '../../constants/nextBestAction.js';
import { formatClockTime } from '../../utils/formatClockTime.js';
import NextBestActionActionButton from './NextBestActionActionButton.jsx';
import NextBestActionCarouselNav from './NextBestActionCarouselNav.jsx';

import { Sparkles } from 'lucide-react';

/**
 * The purple Next Best Action card under the profile header (CT-5713): one
 * suggestion at a time, "N of M" with previous / next, its tags, its primary
 * action and View details, and the footer "Next Best Action · Concierge ·
 * Updated <time>".
 *
 * NOT `NextBestActionWidget` / `CarouselWidget`: those are dashboard widgets
 * with no data contract. The surface is `CartContainer variant="purple"`.
 *
 * The active index is local state, clamped when the list gets shorter (a
 * started workflow drops its suggestion). A suggestion that arrived while the
 * page was open is announced inline ("New action: … · Show"); Show jumps to it
 * and the notice does not come back once the item has been shown. The item
 * keeps its "New" chip while the payload says it is new.
 *
 * Collapsed, the card is ONE line as in the prototype: the title cut short, the
 * counter and the primary action beside it. The rest stays mounted but hidden
 * and `inert`, View details included.
 */
const NextBestActionCard = memo(function NextBestActionCard({
  items,
  updatedAt,
  restricted = false,
  collapsed = false,
  entity,
  onAction,
  onOpenDetails,
}) {
  const { t, i18n } = useTranslation('unified-profiles');
  const [requestedIndex, setRequestedIndex] = useState(0);
  const [seenIds, setSeenIds] = useState(() => new Set());

  const index = Math.min(requestedIndex, items.length - 1);
  const item = items[index];

  const markSeen = useCallback((id) => setSeenIds((current) => (current.has(id) ? current : new Set(current).add(id))), []);
  const goTo = useCallback(
    (next) => {
      setRequestedIndex(next);
      if (items[next]?.isNew) markSeen(items[next].id);
    },
    [items, markSeen],
  );
  const handlePrevious = useCallback(() => goTo(Math.max(0, index - 1)), [goTo, index]);
  const handleNext = useCallback(() => goTo(Math.min(items.length - 1, index + 1)), [goTo, index, items.length]);

  const newIndex = useMemo(
    () => items.findIndex((candidate, position) => candidate.isNew && position !== index && !seenIds.has(candidate.id)),
    [items, index, seenIds],
  );
  const showNew = useCallback(() => goTo(newIndex), [goTo, newIndex]);

  const handleDetails = useCallback(() => onOpenDetails(item), [onOpenDetails, item]);

  const tags = useMemo(() => {
    if (!item) return [];
    if (!item.reviewer) return item.tags;
    // Someone else is on the draft: that is what the user needs to know, not that it is ready.
    return [
      { key: 'reviewer', label: t('profile.nba.reviewing', { name: item.reviewer.name }) },
      ...item.tags.filter((tag) => tag.key !== 'draft_ready'),
    ];
  }, [item, t]);

  if (!item) return null;

  const updated = formatClockTime(updatedAt, i18n?.language);
  const footer = [t('profile.nba.footer', { time: updated ?? '—' }), restricted ? t('profile.nba.restricted') : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <section
      data-testid="nba-card"
      data-collapsed={collapsed ? 'true' : 'false'}
      aria-roledescription="carousel"
      aria-label={t('profile.nba.label')}
      className="min-w-0 flex-1"
    >
      <CartContainer size={collapsed ? 'S' : 'M'} variant="purple" className="h-full">
        <div
          data-testid="nba-card-body"
          className={collapsed ? 'flex h-full flex-row items-center gap-3' : 'flex h-full flex-col gap-3'}
        >
          <div
            data-testid="nba-card-head"
            // Folded, the head takes the line beside the action; expanded it keeps its
            // height, so a taller sibling's spare room goes above the footer (`mt-auto`).
            className={`flex min-w-0 justify-between gap-3 ${collapsed ? 'flex-1 items-center' : 'items-start'}`}
          >
            <div className="flex min-w-0 items-center gap-2">
              <Sparkles size={16} aria-hidden className="shrink-0 text-purple-600 dark:text-purple-300" />
              <h2 className={`min-w-0 text-base font-semibold text-content ${collapsed ? 'truncate' : ''}`}>
                {item.title}
              </h2>
              {item.isNew ? (
                <Badge variant="purple" size="small">
                  {t('profile.nba.new')}
                </Badge>
              ) : null}
            </div>
            <NextBestActionCarouselNav
              index={index}
              total={items.length}
              previousTitle={items[index - 1]?.title}
              nextTitle={items[index + 1]?.title}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>

          {/* Folded, not unmounted: the class hides it in the browser (a display
              utility would beat the bare attribute), the attribute in tests. */}
          <div className={collapsed ? 'hidden' : 'flex flex-col gap-3'} hidden={collapsed} inert={collapsed ? '' : undefined}>
            {item.description ? <p className="text-sm text-content-secondary">{item.description}</p> : null}
            {item.summary ? <p className="text-sm text-content">{item.summary}</p> : null}
            {tags.length ? (
              <div className="flex flex-wrap items-center gap-2">
                {tags.map((tag) => (
                  <Badge key={`${tag.key}-${tag.label}`} variant={NBA_TAG_VARIANTS[tag.key] ?? 'neutral'} size="small">
                    {tag.label}
                  </Badge>
                ))}
              </div>
            ) : null}
            {newIndex >= 0 ? (
              <p data-testid="nba-new-notice" className="flex items-center gap-1 text-sm text-content">
                {t('profile.nba.newNotice', { title: items[newIndex].title })}
                <Button variant="ghost" size="sm" onClick={showNew}>
                  {t('profile.nba.show')}
                </Button>
              </p>
            ) : null}
          </div>

          <div className={collapsed ? 'flex shrink-0 items-center' : 'mt-auto flex flex-wrap items-center justify-between gap-3'}>
            <div className="flex items-center gap-2">
              <NextBestActionActionButton
                item={item}
                action={item.primaryAction}
                entity={entity}
                onAction={onAction}
              />
              <span className={collapsed ? 'hidden' : 'contents'} hidden={collapsed} inert={collapsed ? '' : undefined}>
                <Button variant="ghost" size="sm" onClick={handleDetails}>
                  {t('profile.nba.actions.view_details')}
                </Button>
              </span>
            </div>
            <span className={collapsed ? 'hidden' : 'text-xs text-content-muted'} hidden={collapsed}>
              {footer}
            </span>
          </div>
        </div>
      </CartContainer>
    </section>
  );
});

export default NextBestActionCard;
